import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import type {
  BulkActionItemResult,
  BulkActionSession,
} from '@web-config-app/core-react';
import { useGetEntityTypeName } from '@web-config-app/core-react';
import type {
  BulkActionOperationType,
  EnvironmentKey,
} from '@web-config-app/core';
import type { StatusReportTableItem } from '@web-config-app/core-react-ui';
import { useGetEntityDetailPath } from '../../hooks/use-get-entity-detail-path/use-get-entity-detail-path';

const skippedMessageKeyByOperationType: Record<
  BulkActionOperationType,
  string
> = {
  import: 'BULK_ACTION_REPORT_ALREADY_EXISTS',
  export: 'BULK_ACTION_REPORT_ALREADY_EXPORTED',
  copyToEnvironment: 'BULK_ACTION_REPORT_ALREADY_EXISTS',
  clone: 'BULK_ACTION_REPORT_ALREADY_EXISTS',
  publish: 'BULK_ACTION_REPORT_ALREADY_PUBLISHED',
  archive: 'BULK_ACTION_REPORT_ALREADY_ARCHIVED',
  delete: 'BULK_ACTION_REPORT_ALREADY_DELETED',
};

const operationNameKeyByOperationType: Record<BulkActionOperationType, string> =
  {
    import: 'BULK_ACTION_REPORT_OPERATION_TYPE_IMPORT',
    export: 'BULK_ACTION_REPORT_OPERATION_TYPE_EXPORT',
    copyToEnvironment: 'BULK_ACTION_REPORT_OPERATION_TYPE_COPY',
    clone: 'BULK_ACTION_REPORT_OPERATION_TYPE_DUPLICATE',
    publish: 'BULK_ACTION_REPORT_OPERATION_TYPE_PUBLISH',
    archive: 'BULK_ACTION_REPORT_OPERATION_TYPE_ARCHIVE',
    delete: 'BULK_ACTION_REPORT_OPERATION_TYPE_DELETE',
  };

// ── Per-item mapping helpers ────────────────────────────────────────

interface MapItemContext {
  operationType: BulkActionOperationType;
  sourceEnvironment?: EnvironmentKey;
  targetEnvironment?: EnvironmentKey;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  getEntityTypeName: (entityType: string) => string;
  getEntityPath: (
    entityType: string,
    instanceId: string,
    environment?: EnvironmentKey,
  ) => string | undefined;
}

// Error rows show a non-linked label and a "See source" detail link.
const mapErrorItem = (
  item: BulkActionItemResult,
  displayId: string,
  entityTypeName: string,
  {
    operationType,
    sourceEnvironment,
    formatMessage,
    getEntityPath,
  }: MapItemContext,
): StatusReportTableItem => {
  const sourceEntityPath = getEntityPath(
    item.entityType,
    item.sourceEntityId,
    sourceEnvironment,
  );

  const detailsLink = sourceEntityPath
    ? {
        label: formatMessage(
          { id: 'BULK_ACTION_REPORT_SEE_SOURCE_ENTITY' },
          { entityType: entityTypeName },
        ),
        path: sourceEntityPath,
        indicator: 'externalLink' as const,
        underlined: true,
      }
    : undefined;

  const operationName = formatMessage({
    id: operationNameKeyByOperationType[operationType],
  });

  const errorText = item.error?.detail
    ? formatMessage(
        { id: 'BULK_ACTION_REPORT_ERROR_WITH_DETAIL' },
        {
          operation: operationName,
          entityType: entityTypeName,
          detail: item.error.detail,
        },
      )
    : formatMessage(
        { id: 'BULK_ACTION_REPORT_ERROR_FALLBACK' },
        { operation: operationName, entityType: entityTypeName },
      );

  return {
    id: item.sourceEntityId,
    status: 'error',
    linkableText: {
      label: displayId,
    },
    details: {
      text: errorText,
      link: detailsLink,
      secondaryText: item.error?.secondaryDetail,
    },
  };
};

// Success, skipped, and loading rows share the same shape — only the
// link path and details text differ.
const mapNonErrorItem = (
  item: BulkActionItemResult,
  displayId: string,
  entityTypeName: string,
  {
    operationType,
    sourceEnvironment,
    targetEnvironment,
    formatMessage,
    getEntityPath,
  }: MapItemContext,
): StatusReportTableItem => {
  // entityId is the destination entity produced by the operation (import/clone/copy).
  // Export doesn't create a new entity, so we fall back to the source.
  const resolvedEntityId = item.entityId ?? item.sourceEntityId;

  // publish/export/clone/archive/delete link to the source environment.
  // import/copyToEnvironment link to the target environment.
  const linkEnvironment =
    operationType === 'export' ||
    operationType === 'clone' ||
    operationType === 'publish' ||
    operationType === 'archive' ||
    operationType === 'delete'
      ? sourceEnvironment
      : targetEnvironment;

  // Deleted entities no longer exist, so linking to them would 404.
  const linkPath =
    item.status !== 'loading' && operationType !== 'delete'
      ? item.link ??
        getEntityPath(item.entityType, resolvedEntityId, linkEnvironment)
      : undefined;

  const resolvedEntityName = item.entityName || item.sourceEntityId || '--';

  const entityLabel = formatMessage(
    { id: 'BULK_ACTION_REPORT_ITEM_LABEL' },
    { entityType: entityTypeName, entityName: resolvedEntityName },
  );

  let detailsText = entityLabel;
  if (item.status === 'skipped') {
    detailsText = item.error?.detail
      ? `${entityLabel} — ${item.error.detail}`
      : formatMessage(
          { id: skippedMessageKeyByOperationType[operationType] },
          { entityType: entityTypeName, entityName: resolvedEntityName },
        );
  }

  return {
    id: item.sourceEntityId,
    status: item.status,
    linkableText: {
      label: displayId,
      path: linkPath,
      indicator: linkPath ? 'externalLink' : undefined,
    },
    details: {
      text: detailsText,
    },
  };
};

// Routes a single item result to the appropriate mapping function.
const mapItemToRow = (
  item: BulkActionItemResult,
  context: MapItemContext,
): StatusReportTableItem => {
  const displayId =
    item.domainEntityId ??
    (item.status !== 'loading' ? item.sourceEntityId : undefined) ??
    '--';
  const entityTypeName = context.getEntityTypeName(item.entityType);

  return item.status === 'error'
    ? mapErrorItem(item, displayId, entityTypeName, context)
    : mapNonErrorItem(item, displayId, entityTypeName, context);
};

// ── Hook ────────────────────────────────────────────────────────────

/**
 * Maps a bulk action session's item results into StatusReportTableItem rows
 * for display in the reporting table. Resolves entity type names, detail
 * paths, and i18n text internally so callers only need to pass the session.
 */
export const useMapSessionItemsToRows = (
  session: BulkActionSession,
): StatusReportTableItem[] => {
  const { operationType, itemResults, sourceEnvironment, targetEnvironment } =
    session;

  const { formatMessage } = useIntl();
  const getEntityTypeName = useGetEntityTypeName();
  const getEntityPath = useGetEntityDetailPath();

  const rows = React.useMemo(() => {
    const context: MapItemContext = {
      operationType,
      sourceEnvironment,
      targetEnvironment,
      formatMessage,
      getEntityTypeName,
      getEntityPath,
    };

    return itemResults.map((item) => mapItemToRow(item, context));
  }, [
    itemResults,
    operationType,
    sourceEnvironment,
    targetEnvironment,
    formatMessage,
    getEntityTypeName,
    getEntityPath,
  ]);

  return rows;
};
